// Sentry + PostHog telemetry — Averdy Agency frontend.
//
// Both integrations are graceful-degrade: without the corresponding
// REACT_APP_SENTRY_DSN / REACT_APP_POSTHOG_KEY env vars, the SDKs are not
// initialised and every helper below is a no-op. The app runs perfectly with
// zero telemetry configured.
import * as Sentry from "@sentry/react";
import posthog from "posthog-js";

const sentryDsn = (process.env.REACT_APP_SENTRY_DSN || "").trim();
const posthogKey = (process.env.REACT_APP_POSTHOG_KEY || "").trim();
const posthogHost = (process.env.REACT_APP_POSTHOG_HOST || "https://eu.posthog.com").replace(/\/$/, "");

const secretKey = /(iban|email|cookie|authorization|token|secret|password|jwt|body|bic|counterparty|beneficiary)/i;

function scrub(value) {
  if (Array.isArray(value)) return value.map(scrub);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([k, v]) => [
        k, secretKey.test(k) ? "[REDACTED]" : scrub(v),
      ])
    );
  }
  if (typeof value === "string") {
    return value
      .replace(/\b[A-Z]{2}\d{2}[A-Z0-9]{10,30}\b/gi, "[IBAN_REDACTED]")
      .replace(/\b[^\s@]+@[^\s@]+\.[^\s@]+\b/g, "[EMAIL_REDACTED]");
  }
  return value;
}

if (sentryDsn) {
  try {
    Sentry.init({
      dsn: sentryDsn,
      environment: process.env.REACT_APP_SENTRY_ENV || "preview",
      sampleRate: 1.0,
      tracesSampleRate: 0.2,
      replaysSessionSampleRate: 0,     // never record normal sessions
      replaysOnErrorSampleRate: 1.0,   // 100% of error sessions
      sendDefaultPii: false,
      integrations: [
        Sentry.replayIntegration({
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,
          block: [".sentry-block", "[data-sentry-block]", ".bank-details", ".iban"],
        }),
      ],
      beforeSend(event) {
        const clean = scrub(event);
        if (clean.user) delete clean.user.email;
        if (clean.request) {
          delete clean.request.cookies;
          delete clean.request.headers;
          delete clean.request.data;
        }
        return clean;
      },
    });
  } catch (e) {
    // Never let telemetry break the app boot.
    console.warn("Sentry init failed — continuing without it.", e);
  }
}

if (posthogKey) {
  try {
    posthog.init(posthogKey, {
      api_host: posthogHost,
      autocapture: true,
      capture_pageview: true,
      disable_session_recording: true,  // RGPD — no session recording
      secure_cookie: true,
      before_send(event) {
        if (!event) return null;
        event.properties = scrub(event.properties || {});
        delete event.properties.email;
        delete event.properties.cookie;
        return event;
      },
    });
  } catch (e) {
    console.warn("PostHog init failed — continuing without it.", e);
  }
}

export function identifyUser({ userId, tenantId, role }) {
  if (!posthogKey || !userId) return;
  try {
    posthog.identify(String(userId), {
      tenant_id: String(tenantId), role: String(role || ""),
    });
  } catch { /* ignore */ }
}

export function track(event, properties = {}, context = {}) {
  if (!posthogKey) return;
  try {
    posthog.capture(event, scrub({
      ...properties,
      tenant_id: context.tenantId,
      role: context.role,
    }));
  } catch { /* ignore */ }
}

export const TelemetryErrorBoundary = Sentry.ErrorBoundary;
